import type { ButtonHTMLAttributes } from 'react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon, type IconSize } from '../Icon';

/** Figma "Icon Button" (20:36): 44px circle. `white` has a soft outline + shadow, `ghost` is bare. */
export type IconButtonVariant = 'white' | 'ghost';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: PhosphorIcon;
  /** Required: an icon-only control has no visible text for assistive tech. */
  'aria-label': string;
  variant?: IconButtonVariant;
  iconSize?: IconSize;
}

export function IconButton({
  icon,
  variant = 'white',
  iconSize = 'md',
  type = 'button',
  className,
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex size-(--mbm-size-icon-button) shrink-0 items-center justify-center rounded-full text-text-primary',
        'transition-opacity duration-(--mbm-duration-fast) hover:opacity-(--mbm-opacity-pressed)',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        'disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:opacity-100',
        variant === 'white' && 'border border-border-line bg-surface-card shadow-(--mbm-shadow-sm)',
        className,
      )}
      {...props}
    >
      <Icon icon={icon} size={iconSize} />
    </button>
  );
}
